import { Avatar, Button, Modal } from '@mantine/core';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  buildPresetPermissions,
  PERMISSION_SUBJECTS,
  PermissionsEditor,
  PermissionsState,
} from 'src/Components/Admin/PermissionsEditor';
import { useUpdateMemberPermissions } from 'src/Hooks/Mutations/useUpdateMemberPermissions';
import {
  PermissionEntry,
  PermissionEntryLevelEnum,
  UpdateMemberPermissionsDtoPresetEnum,
} from 'src/Services/API/generated';

import styles from './EditPermissionsModal.module.scss';
import { Props } from './EditPermissionsModal.types';

const EditPermissionsModal = ({ open, onClose, member }: Props) => {
  const { t } = useTranslation();
  const { mutate, isLoading } = useUpdateMemberPermissions({
    onSuccess: () => {
      onClose();
    },
  });

  const [permissions, setPermissions] = useState<PermissionsState>(() =>
    buildPresetPermissions(UpdateMemberPermissionsDtoPresetEnum.Contributor)
  );

  // Initialize permissions from member data when modal opens
  useEffect(() => {
    if (member?.permissions && open) {
      const memberPerms = member.permissions as Record<string, string>;
      setPermissions(
        PERMISSION_SUBJECTS.reduce(
          (acc, subject) => ({
            ...acc,
            [subject]:
              (memberPerms[subject] as PermissionEntryLevelEnum) ?? PermissionEntryLevelEnum.Own,
          }),
          {} as PermissionsState
        )
      );
    }
  }, [member, open]);

  const handleSave = () => {
    if (!member) return;

    const permissionEntries: PermissionEntry[] = PERMISSION_SUBJECTS.map(subject => ({
      subject,
      level: permissions[subject],
    }));

    mutate({
      membershipId: member.id,
      permissions: permissionEntries,
    });
  };

  return (
    <Modal
      opened={open}
      onClose={onClose}
      title={
        <div className={styles.title}>
          {t('permissions.modal.title', 'Modifier les permissions')}
        </div>
      }
      size="lg"
      withCloseButton={true}
    >
      {member && (
        <>
          <div className={styles.memberInfo}>
            <Avatar src={member.user.profilePictureUrl} size="md" radius="xl">
              {member.user.firstName?.[0]}
              {member.user.lastName?.[0]}
            </Avatar>
            <div>
              <div className={styles.memberName}>
                {member.user.firstName} {member.user.lastName}
              </div>
              <div className={styles.memberEmail}>{member.user.email}</div>
            </div>
          </div>

          <PermissionsEditor value={permissions} onChange={setPermissions} />

          <div className={styles.buttons}>
            <Button variant="light" onClick={onClose} disabled={isLoading}>
              {t('common.cancel', 'Annuler')}
            </Button>
            <Button onClick={handleSave} loading={isLoading}>
              {t('common.save', 'Enregistrer')}
            </Button>
          </div>
        </>
      )}
    </Modal>
  );
};

export default EditPermissionsModal;
